import React from "react";
import { ThemeProvider } from "onnit-ui/lib/themes/styled";
import GlobalStyle from "onnit-ui/lib/themes/GlobalStyle";
import theme from "onnit-ui/lib/themes/light";
import Box from "onnit-ui/lib/box/Box";
import useOnnitContext from "onnit-ui/lib/hooks/useOnnitContext";
import usePageData from "../../hooks/usePageData";
import { Img, Picture } from "onnit-ui/lib/image";
import ProductImageGrid from "onnit-ui/lib/module/ProductImageGrid";
import SimpleProductPageTop from "../../components/common/layouts/SimpleProductPageTop";

import Grid from "onnit-ui/lib/module/grid/12/Grid";
import TwelveColumn from "onnit-ui/lib/module/grid/12/Grid";
import SingleProductBuyBox from "onnit-ui/lib/buybox/SingleProductBuyBox";
import AddToCartButton from "../../components/common/AddToCartButton";
import Product from "onnit-ui/lib/@types/interfaces/product/BuyBoxProduct";
import Text, { List, ListItem, Sup } from "onnit-ui/lib/text";
import LabelContent from "onnit-ui/lib/module/LabelContent";
import MultiColumnText from "onnit-ui/lib/text/MultiColumnText";
import MediaCollage01 from "onnit-ui/lib/module/MediaCollage01";
import MediaCollage03 from "onnit-ui/lib/module/MediaCollage03";
import Embed from "onnit-ui/lib/video/Embed";
import ImageContentPortrait from "onnit-ui/lib/module/ImageContentPortrait";
import SupplementFacts from "../../components/common/SupplementFacts";
import UsageAndDisclaimers from "../../components/common/UsageAndDisclaimers";
import HubSpotEmailCapture from "../../components/common/HubSpotEmailCapture";
import { CartProductConfig } from "../../interfaces/CartProductConfig";
import FiveStarTestimonials from "../../components/common/FiveStarTestimonials";
import testimonials from "./testimonials";
import ProductBoxDisplay from "../../components/common/ProductBoxDisplay";
import { ProductBox } from "../../interfaces/ProductBox";
import dayPackIngredients from "./day-pack-ingredients";
import nightPackIngredients from "./night-pack-ingredients";
import OnnitLogoBackground from "../../components/common/OnnitLogoBackground";

let dayIngredients: ProductBox[] = dayPackIngredients;
let nightIngredients: ProductBox[] = nightPackIngredients;

export function TrialDisclaimerText({ product, color = "grays.5" }: { product: Product; color?: string }) {
    const calculatePrice = (priceMsrp: string) => {
        const price = parseFloat(priceMsrp);
        return price - price * 0.15;
    };

    return (
        <>
            <Text fontSize={0} lineHeight={6} color={color} mb={4}>
                Customers signing up will receive a free 7-Day Supply, and we’ll send it for just the cost of shipping
                and tax. (Domestic orders only).
            </Text>
            <Text fontSize={0} lineHeight={6} color={color} mb={4}>
                This free trial purchase includes a subscription that autoships Total Human
                <Sup variant="reg" /> 7-day supply in 17 days then every 7 days at 15% off MSRP ($
                {calculatePrice(product.price_msrp_raw).toFixed(2)}) + shipping & tax. (Domestic orders only).
            </Text>
            <Text fontSize={0} lineHeight={6} color={color} mb={4}>
                Cancel the subscription any time within the first 17 days of ordering and we won’t send any additional
                product - it&rsquo;s really that simple! Should the subscription remain active and recurring orders
                process but you do not wish to keep your subscription active, unopened product can be returned for a
                refund within 90 days and your subscription can be cancelled.
            </Text>
            <Text fontSize={0} lineHeight={6} color={color}>
                <a href="/terms-and-conditions/subscription/" target="_blank">
                    Read more details about our subscription program <span style={{ fontWeight: "bold" }}>here</span>.
                </a>
            </Text>
        </>
    );
}

const CustomEmailListSignup = ({ product, textColor = "grays.5" }: { product: Product; textColor?: string }) => {
    const inStock = product.in_stock;

    return (
        <>
            {inStock && (
                <>
                    <Text as="p" my={1} color={textColor}>
                        Enter your email below to get started.
                    </Text>
                    <HubSpotEmailCapture
                        pageName="Total Human® Trial"
                        portalId="2252779"
                        formId="eefee1e6-5b81-487c-b0da-784861c137c1"
                        buttonLabel="Get access"
                        successFeedbackColor="black"
                        onSuccess={() => addTotalHumanTrialToCart(product.id)}
                    />
                    <Box py={[4, 4, 6]}>
                        <TrialDisclaimerText product={product} color={textColor} />
                    </Box>
                </>
            )}

            {!inStock && (
                // Show OOS product box instead of email add to cart capture
                <CustomProductBox product={product} textColor="black" buttonColor="black" buttonTextColor="white" />
            )}
        </>
    );
};

interface CustomProductBoxProps {
    product: Product;
    buttonColor?: string;
    buttonTextColor?: string;
    textColor?: string;
}

const CustomProductBox = ({ product, buttonColor, buttonTextColor, textColor }: CustomProductBoxProps) => {
    return (
        <>
            <SingleProductBuyBox
                mb={6}
                product={product}
                renderInStockView={(product: Product) => {
                    return (
                        <AddToCartButton
                            onSuccess={() => {
                                window.location.href = "/cart/checkout/shipping-address";
                            }}
                            width={1}
                            size="large"
                            buyBtnText="Get it Free"
                            buttonColor={buttonColor}
                            buttonTextColor={buttonTextColor}
                            configs={[
                                {
                                    product_id: product.id,
                                    quantity: 1,
                                    subscription_interval: null,
                                },
                            ]}
                        />
                    );
                }}
            />
            <TrialDisclaimerText product={product} color={textColor} />
        </>
    );
};

function addTotalHumanTrialToCart(pid: number) {
    // Push this check to when the user clicks the button
    // because ONNIT.cart is defined after this app bootstraps.
    if (!window.ONNIT || !window.ONNIT.cart) {
        throw new Error("ONNIT.cart is undefined");
    }

    const config: CartProductConfig = {
        product_id: pid,
        quantity: 1,
        subscription_interval: null,
    };

    window.ONNIT.cart
        .addProducts([config])
        .then(() => {
            window.location.href = "/cart/checkout/shipping-address";
        })
        .catch((error) => console.error("Failed to add product to cart.", error));
}

interface CustomAddToBagSectionProps {
    imgUrl?: string;
    altText?: string;
    buttonColor?: string;
    buttonTextColor?: string;
    headlineColor?: string;
    textColor?: string;
}

const CustomAddToBagSection: React.FC<CustomAddToBagSectionProps> = ({
    children,
    imgUrl,
    altText,
    buttonColor,
    buttonTextColor,
    headlineColor = "black",
    textColor = "grays.5",

    ...rest
}) => {
    const onnitContext = useOnnitContext();
    const { pageData } = usePageData();
    const isSignupPage = window.location.href.indexOf("get-total-human-trial") > -1;

    if (!pageData || !onnitContext) return null;

    return (
        <>
            <Box {...rest}>
                {imgUrl && (
                    <Grid py={[6, 6, 8, 10]}>
                        {imgUrl && (
                            <Box gridColumn={["1 / -1", "1 / -1", "1 / -1", "2 / span 6"]} px={4} mb={6}>
                                <Img placeholder={[688, 380]} alt={altText} src={imgUrl} />
                            </Box>
                        )}
                        <Box gridColumn={["1 / -1", "1 / -1", "1 / -1", "8 / span 4"]} px={[4, 4, 6]}>
                            {children && (
                                <Text
                                    color={headlineColor}
                                    fontStyle="italic"
                                    fontWeight="bold"
                                    lineHeight={1}
                                    fontSize={[6, 8]}
                                    mb={6}
                                >
                                    {children}
                                </Text>
                            )}

                            {isSignupPage && (
                                <Box px={[4, 4, 0]}>
                                    <CustomEmailListSignup textColor={textColor} product={pageData.products[0]} />
                                </Box>
                            )}

                            {!isSignupPage && (
                                <Box py={4}>
                                    <CustomProductBox
                                        product={pageData.products[0]}
                                        buttonColor={buttonColor}
                                        buttonTextColor={buttonTextColor}
                                        textColor={textColor}
                                    />
                                </Box>
                            )}
                        </Box>
                    </Grid>
                )}

                {!imgUrl && (
                    <Grid>
                        <Box gridColumn={["1 / -1"]}>
                            {children && (
                                <Text
                                    color={headlineColor}
                                    fontStyle="italic"
                                    fontWeight="bold"
                                    lineHeight={1}
                                    fontSize={[6, 8]}
                                    mb={6}
                                >
                                    {children}
                                </Text>
                            )}

                            {isSignupPage && (
                                <Box>
                                    <CustomEmailListSignup textColor={textColor} product={pageData.products[0]} />
                                </Box>
                            )}
                            {!isSignupPage && (
                                <CustomProductBox
                                    product={pageData.products[0]}
                                    buttonColor={buttonColor}
                                    buttonTextColor={buttonTextColor}
                                    textColor={textColor}
                                />
                            )}
                        </Box>
                    </Grid>
                )}
            </Box>
        </>
    );
};

function Page() {
    const onnitContext = useOnnitContext();
    const { pageData } = usePageData();

    if (!pageData || !onnitContext) return null;

    const IMG_PARAMS = "?q=40&fm=pjpg&auto=compress,format";
    const IMG_URL = onnitContext.url.cdn.static_images + "/product-page/total-human-trial";
    const darkGreen = "#1E2B1B";
    const darkTeal = "#54C2B7";
    const sunwashGolder = "#D0B16E";

    return (
        <ThemeProvider theme={theme}>
            <>
                <GlobalStyle />

                <SimpleProductPageTop
                    nameAsElement="h1"
                    buyAreaChild={
                        <>
                            <CustomAddToBagSection />
                        </>
                    }
                >
                    <ProductImageGrid>
                        <Picture
                            lazyLoad={false}
                            width={944}
                            height={424}
                            alt="Total Human® Box"
                            src={[
                                `${IMG_URL}/total-human-box-gallery-1--mobile.png${IMG_PARAMS}`,
                                `${IMG_URL}/total-human-box--gallery1.jpg${IMG_PARAMS}`,
                            ]}
                        />
                        <Img
                            lazyLoad={false}
                            src={`${IMG_URL}/day-pack--gallery3.png${IMG_PARAMS}`}
                            alt="Total Human® Day pack opened"
                            width={920}
                            height={920}
                        />
                        <Img
                            lazyLoad={false}
                            src={`${IMG_URL}/night-pack--gallery4.png${IMG_PARAMS}`}
                            alt="Total Human® night pack opened"
                            width={920}
                            height={920}
                        />

                        <Picture
                            lazyLoad={false}
                            width={944}
                            height={424}
                            alt="MMA Girl throwing a punch"
                            src={[
                                `${IMG_URL}/mma-girl-gallery-4--mobile.png${IMG_PARAMS}`,
                                `${IMG_URL}/mma-girl--gallery2.jpg${IMG_PARAMS}`,
                            ]}
                        />
                    </ProductImageGrid>
                </SimpleProductPageTop>

                <div className="hide-ie">
                    <Grid display={"grid"} mt={[8, 8, 10]} mb={6}>
                        <Box gridColumn={["1 / -1", "1 / -1", "2 / -1", "3 / span 9"]} px={4}>
                            <Text as="h2" typeStyle="headlineHero01" textTransform="uppercase">
                                Optimization
                                <br /> On Autopilot
                            </Text>
                        </Box>
                    </Grid>

                    <Box bg="black" py={[6, 6, 6, 8]}>
                        <LabelContent
                            label={
                                <Text typeStyle="copy05" color={darkTeal}>
                                    Total Human
                                    <Sup variant="reg" />
                                </Text>
                            }
                            content={
                                <>
                                    <Text as="p" typeStyle="copy02" color="white" mb={4}>
                                        It&rsquo;s hard to get all the nutrition you need in a day to think, feel, and
                                        perform at your best. It&rsquo;s even harder if you’re an athlete or busy person
                                        who experiences more stress than most other people, and whose nutritional needs
                                        are greater. For people like you, supplementation is practically a necessity.
                                    </Text>
                                    <Text as="p" typeStyle="copy02" color="white" mb={4}>
                                        So you buy vitamins. And minerals. And enzymes. And herbal extracts. And healthy
                                        fats... And man does it get expensive! Plus, unless you break out a calculator
                                        and consult the Daily Value tables for all these nutrients, you have no way of
                                        knowing how much you need of each one or how close you are to obtaining it. Are
                                        you getting enough vitamin D? Are you getting too much calcium?
                                    </Text>
                                    <Text as="p" typeStyle="copy02" color="white">
                                        <Text
                                            as="span"
                                            fontWeight="bold"
                                            display="inline"
                                            typeStyle="copy02"
                                            color={darkTeal}
                                        >
                                            The answer is Total Human
                                            <Sup variant="reg" />, a collection of only the essential and
                                            clinically-studied vitamins, minerals, and other botanical nutrients that
                                            have been shown to be beneficial,
                                        </Text>{" "}
                                        all together in one place. Total Human
                                        <Sup variant="reg" /> is a complete re-imagining of the daily multivitamin,
                                        combining several of Onnit&rsquo;s best-in-class formulas into an all-in-one
                                        product that&rsquo;s specifically balanced to support the brain, bones, and
                                        joints, and aid mood, energy, and immune health.
                                        <Sup variant="dagger" />
                                    </Text>
                                </>
                            }
                        />
                    </Box>

                    <Box mx="auto" mt={8}>
                        <Text
                            textAlign="center"
                            typeStyle="copy05"
                            color="grays.5"
                            style={{
                                letterSpacing: "0.25em",
                                fontStyle: "italic",
                            }}
                            fontSize={["12px", "12px", "12px", "12px"]}
                            textTransform="uppercase"
                        >
                            Ingredients
                        </Text>
                        <Text
                            textAlign="center"
                            typeStyle="copy01"
                            fontSize={["24px", "24px", "24px", "24px"]}
                            style={{
                                fontWeight: 900,
                                fontStyle: "italic",
                            }}
                            textTransform="uppercase"
                        >
                            Day Pack
                        </Text>

                        <Box
                            px={[1, 6, 8]}
                            display="grid"
                            gridTemplateColumns="repeat(auto-fit, minmax(320px, 1fr))"
                            mt={[6, 6, 6, 8]}
                        >
                            {dayIngredients.map((ingredient, index) => (
                                <ProductBoxDisplay
                                    key={index}
                                    title={ingredient.title}
                                    content={ingredient.content}
                                    imgUrl={`${IMG_URL}/${ingredient.img}${IMG_PARAMS}`}
                                    altText={ingredient.altText}
                                />
                            ))}
                        </Box>
                    </Box>

                    <Box mx="auto" mt={[6, 6, 8]} pt={8} backgroundColor="grays.0">
                        <Text
                            textAlign="center"
                            typeStyle="copy05"
                            color="grays.5"
                            style={{
                                letterSpacing: "0.25em",
                                fontStyle: "italic",
                            }}
                            fontSize={["12px", "12px", "12px", "12px"]}
                            textTransform="uppercase"
                        >
                            Ingredients
                        </Text>
                        <Text
                            textAlign="center"
                            typeStyle="copy01"
                            fontSize={["24px", "24px", "24px", "24px"]}
                            style={{
                                fontWeight: 900,
                                fontStyle: "italic",
                            }}
                            textTransform="uppercase"
                        >
                            Night Pack
                        </Text>

                        <Box
                            px={[1, 6, 8]}
                            display="grid"
                            gridTemplateColumns="repeat(auto-fit, minmax(320px, 1fr))"
                            mt={[6, 6, 6, 8]}
                        >
                            {nightIngredients.map((ingredient, index) => (
                                <ProductBoxDisplay
                                    key={index}
                                    title={ingredient.title}
                                    content={ingredient.content}
                                    imgUrl={`${IMG_URL}/${ingredient.img}${IMG_PARAMS}`}
                                    altText={ingredient.altText}
                                />
                            ))}
                        </Box>
                    </Box>

                    <TwelveColumn my={[6, 6, 6, 8]} pt={[6, 6, 2]}>
                        <Box gridColumn={["1 / -1", "1/-1", "1/-1", "3 /span 8"]} px={[4]}>
                            <MultiColumnText>
                                <Text as="p" typeStyle="copy02" fontWeight="regular" mb={4}>
                                    Not a &ldquo;magic pill,&rdquo; the Total Human
                                    <Sup variant="reg" /> formulas come in convenient day and night packs, to ensure
                                    that your body gets the right nutrition it needs at the times it most needs it. The
                                    Day Pack aims to help you be alert in the morning, focused during work, and
                                    energized for physical activity, while the Night Pack assists with winding down and
                                    sleeping soundly.
                                    <Sup variant="dagger" />
                                </Text>
                                <Text as="p" typeStyle="copy02" fontWeight="regular">
                                    If you&rsquo;re tired of buying dozens of different brands to cover all your
                                    nutrition bases, or you want to simplify your supplementation with one product,
                                    Total Human
                                    <Sup variant="reg" /> is for you. It&rsquo;s the easiest way to get optimized.
                                </Text>
                            </MultiColumnText>
                        </Box>
                        <Box gridColumn={["1 / -1", "1/-1", "1/-1", "4 /span 6"]}>
                            <Embed
                                title="Total Human® Product Video"
                                src="https://player.vimeo.com/video/466390434"
                                my={6}
                            />
                        </Box>
                    </TwelveColumn>

                    <MediaCollage01
                        mb={6}
                        item1={
                            <Img
                                src={`${IMG_URL}/mma-girl-collage-1.png${IMG_PARAMS}`}
                                alt="MMA Girl punching"
                                width={448}
                                height={692}
                            />
                        }
                        item2={
                            <Img
                                src={`${IMG_URL}/mma-girl-collage-2.png${IMG_PARAMS}`}
                                alt="MMA Girl near window"
                                width={448}
                                height={692}
                            />
                        }
                        item3={
                            <Img
                                src={`${IMG_URL}/rugby-player-collage-3.png${IMG_PARAMS}`}
                                alt="Rugby player passing ball"
                                width={928}
                                height={1280}
                            />
                        }
                    />

                    <Box bg={sunwashGolder}>
                        <CustomAddToBagSection
                            imgUrl={`${IMG_URL}/try-now-ballet-girl.png${IMG_PARAMS}`}
                            altText="Girl practicing ballet"
                            textColor="black"
                            headlineColor="black"
                            buttonColor="black"
                            buttonTextColor="white"
                        >
                            Try Total Human
                            <Sup variant="reg" />
                            <br /> For 7 Days &ndash; FREE!
                        </CustomAddToBagSection>
                    </Box>

                    <OnnitLogoBackground
                        fillColor="#54c2b7"
                        fillOpacity={0.2}
                        bgFillColor="black"
                        backgroundColor="#000000"
                        backgroundSize="cover"
                    >
                        <FiveStarTestimonials
                            titleColor={darkTeal}
                            title={
                                <>
                                    What customers are saying about Total Human
                                    <Sup variant="reg" />
                                </>
                            }
                            testimonials={testimonials}
                            textColor="white"
                        />
                    </OnnitLogoBackground>

                    <LabelContent
                        my={[6, 6, 6, 8]}
                        label={
                            <Text typeStyle="copy05">
                                Total Human
                                <Sup variant="reg" />
                            </Text>
                        }
                        content={
                            <>
                                <Text typeStyle="copyHero01" textTransform="uppercase" mb={4}>
                                    Benefits
                                </Text>
                                <List ml={2} pl={4}>
                                    {[
                                        'Nutritional support for the brain, bones, joints, mood, immune system, and energy.<span class="oui-sup">&dagger;</span>',
                                        "Convenient day and night packs so you know what to take and when.",
                                        'Includes Onnit&rsquo;s most popular supplements all in one place—including Alpha BRAIN<span class="oui-sup">®</span>, Shroom Tech<span class="oui-sup">&reg;</span> SPORT, New MOOD<span class="oui-sup">&reg;</span>, and VIRUTech<span class="oui-sup">&reg;</span>',
                                        "You SAVE, versus buying each component supplement individually.",
                                    ].map((text, i) => (
                                        <ListItem
                                            key={i}
                                            my={1}
                                            color="black"
                                            fontSize={[2, 4, 5]}
                                            dangerouslySetInnerHTML={{ __html: text }}
                                        />
                                    ))}
                                </List>
                            </>
                        }
                    />

                    <MediaCollage03
                        mb={[6, 6, 8, 10]}
                        item1={
                            <Img
                                src={`${IMG_URL}/mma-girl-benefits-collage-1.png${IMG_PARAMS}`}
                                alt="MMA Girl kicking"
                                width={1170}
                                height={656}
                            />
                        }
                        item2={
                            <Img
                                src={`${IMG_URL}/guy-benefits-collage-2.png${IMG_PARAMS}`}
                                alt="Side profile image of man"
                                width={448}
                                height={448}
                            />
                        }
                        item3={
                            <Img
                                src={`${IMG_URL}/pill-pack-benefits-collage-3.png${IMG_PARAMS}`}
                                alt="Hand with open packet of Total Human® day pack"
                                width={688}
                                height={388}
                            />
                        }
                    />

                    <ImageContentPortrait
                        my={[6, 6, 6, 8]}
                        reverse={true}
                        image={
                            <Img
                                src={`${IMG_URL}/hill-runner.png${IMG_PARAMS}`}
                                alt="Man running up city hill"
                                width={928}
                                height={1200}
                            />
                        }
                        content={
                            <Box display="flex" flexDirection="column" height="100%" justifyContent="center">
                                <Text as="p" typeStyle="copy02" mb={4}>
                                    If you&rsquo;re tired of roaming the aisles at health food stores, or hearing from
                                    friends that &ldquo;you&rsquo;re not getting enough [fill in the blank
                                    nutrient],&rdquo; you need to give Total Human
                                    <Sup variant="reg" /> a try.
                                </Text>
                                <Text as="p" typeStyle="copy02" mb={4}>
                                    But we don&rsquo;t expect you to pay hard-earned money for it without knowing for
                                    sure that it works. That&rsquo;s why, for a limited time, you have the chance to try
                                    Total Human
                                    <Sup variant="reg" /> for FREE. Just pay shipping, and you&rsquo;ll get a seven-day
                                    supply at no extra cost.
                                </Text>
                            </Box>
                        }
                    />
                </div>

                <Box bg={sunwashGolder} mt={[6, 6, 6, 10]}>
                    <CustomAddToBagSection
                        imgUrl={`${IMG_URL}/try-now-gamers.jpg${IMG_PARAMS}`}
                        altText="Gamers sitting on couch"
                        textColor="black"
                        headlineColor="black"
                        buttonColor="black"
                        buttonTextColor="white"
                    >
                        Try Total Human
                        <Sup variant="reg" />
                        <br /> For 7 Days &ndash; FREE!
                    </CustomAddToBagSection>
                </Box>

                <Box backgroundColor="black" py={[6, 6, 8]}>
                    <TwelveColumn>
                        <Box gridColumn={["1 / -1", "1 / -1", "1 / -1", "2 / span 9"]} px={4} pt={[4, 4, 4, 6]}>
                            <Text
                                as="h2"
                                fontWeight="black"
                                lineHeight={0}
                                textTransform="uppercase"
                                color={darkTeal}
                                fontSize={[10, 10, 12, 14]}
                                mb={[6, 6, 6, 8]}
                            >
                                How <br />
                                Total Human
                                <Sup variant="reg" />
                                <br /> works
                            </Text>
                        </Box>
                    </TwelveColumn>

                    <ImageContentPortrait
                        pt={[4, 4, 4, 6]}
                        reverse={true}
                        image={
                            <Img
                                src={`${IMG_URL}/day-pack-opened.png${IMG_PARAMS}`}
                                alt="Hand holding Total Human® Day pack pills"
                                width={928}
                                height={1200}
                            />
                        }
                        content={
                            <Box display="flex" flexDirection="column" height="100%" justifyContent="center">
                                <Text as="p" typeStyle="copy02" mb={4} color="white">
                                    Most &ldquo;all-in-one&rdquo; supplements are anything but. They promise to boost
                                    health, but don&rsquo;t promote the function of the overall systems that support
                                    it—that&rsquo;s why you hear reports that &ldquo;multivitamins don&rsquo;t
                                    work.&rdquo; All those products are really good for is filling in the nutritional
                                    gaps in your diet by bombarding you with vitamins and minerals. Total Human
                                    <Sup variant="reg" /> is so much more.
                                </Text>
                                <Text as="p" typeStyle="copy02" mb={4} color="white">
                                    Vitamins and minerals aid the processes in the body that regulate health and
                                    performance, but carefully-selected compounds in Total Human
                                    <Sup variant="reg" /> take nutrition a step further, targeting specific systems that
                                    need support to help optimize your mind, muscles, bones, joints, mood, and overall
                                    energy and well-being.
                                    <Sup variant="dagger" /> It&rsquo;s not a multivitamin, but rather a grouping of
                                    powerful, clinically-studied ingredients that have been curated from Onnit&rsquo;s
                                    most popular existing supplements.
                                </Text>
                                <Text as="p" typeStyle="copy02" mb={4} color="white">
                                    In Alpha BRAIN
                                    <Sup variant="reg" />, for instance, we give you a good dose of vitamin B6, which
                                    aids neurotransmitter production to stimulate cognitive speed, but we also provide
                                    huperzia serrata, a clubmoss that helps stop the breakdown of neurotransmitters.
                                    <Sup variant="dagger" /> Think: more in, less out.
                                </Text>
                                <Text as="p" typeStyle="copy02" mb={4} color="white">
                                    Is your multivitamin this thorough?
                                </Text>
                            </Box>
                        }
                    />
                </Box>

                <Box>
                    <CustomAddToBagSection
                        imgUrl={`${IMG_URL}/bmx-girl.png${IMG_PARAMS}`}
                        altText="Girl on BMX bike"
                        textColor="black"
                        headlineColor="black"
                        buttonColor="black"
                        buttonTextColor="white"
                    >
                        Try Total Human
                        <Sup variant="reg" />
                        <br /> For 7 Days &ndash; FREE!
                    </CustomAddToBagSection>
                </Box>

                <UsageAndDisclaimers supplementFacts={<SupplementFacts enableZoom={true} />}>
                    <Text fontSize={0} color="grays.4" mb={3}>
                        <Sup>*</Sup>Offer valid for qualified customers only. Domestic Orders Only.
                    </Text>
                    <Text
                        fontSize={0}
                        color="grays.4"
                        target="_blank"
                        href="/terms-and-conditions/subscription/"
                        as="a"
                    >
                        Read the Terms and Conditions here.
                    </Text>
                </UsageAndDisclaimers>
            </>
        </ThemeProvider>
    );
}

export default Page;
